import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Flame, Gem, Crown, LogOut, Settings2, UserRound, Moon, Sun, Shield } from "lucide-react";
import Logo from "./atoms/Logo";
import Avatar from "./atoms/Avatar";
import Chip from "./atoms/Chip";
import { isPremium } from "./PremiumGate";
import { getStats } from "../gamification";

// Six hubs — outcome-oriented. Each hub is a landing surface that points
// at existing routes, so deep links keep working.
const PRIMARY_LINKS = [
  { to: "/speak",    label: "Speak",    match: ["/speak", "/conversations", "/conversation-practice", "/oral", "/listening", "/voice-recorder", "/pronunciation", "/speak-practice"] },
  { to: "/write",    label: "Write",    match: ["/write"] },
  { to: "/practice", label: "Practice", match: ["/practice", "/lesson", "/vocabulary", "/stories", "/idioms", "/reading", "/mini-games", "/phrases", "/topics"] },
  { to: "/career",   label: "Career",   match: ["/career"] },
  { to: "/penny",    label: "Penny",    match: ["/penny", "/dashboard", "/progress", "/achievements", "/history", "/calendar", "/bookmarks", "/leaderboard"] },
  { to: "/pricing",  label: "Pricing",  match: ["/pricing"] },
];

export default function Navbar({ user, onLogout, darkMode, setDarkMode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [pathname]);

  const stats = (() => {
    try { return getStats(); } catch { return { streak: 0, gems: 50 }; }
  })();
  const firstName = user?.name?.split(" ")[0] || "Learner";
  const initial = firstName[0]?.toUpperCase() || "L";

  return (
    <nav
      className="relative z-50"
      style={{
        background: "var(--paper)",
        borderBottom: "1px solid var(--border-1)",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between" style={{ height: 64 }}>
        {/* Logo */}
        <Link to="/penny" style={{ textDecoration: "none" }}>
          <Logo />
        </Link>

        {/* Primary links — desktop */}
        <div className="hidden md:flex items-center" style={{ gap: 24 }}>
          {PRIMARY_LINKS.map((l) => {
            const matches = l.match.some((p) => p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(p + "/"));
            return (
              <Link
                key={l.label}
                to={l.to}
                style={{
                  color: matches ? "var(--fg-1)" : "var(--fg-2)",
                  fontWeight: matches ? 700 : 500,
                  fontSize: 14,
                  textDecoration: "none",
                  position: "relative",
                  paddingBottom: 2,
                  borderBottom: matches ? "2px solid var(--gold-500)" : "2px solid transparent",
                  transition: "color 150ms cubic-bezier(.2,.8,.2,1)",
                }}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        {/* Right side */}
        <div className="flex items-center" style={{ gap: 10 }}>
          {/* Streak + gems chips — desktop only */}
          <div className="hidden lg:flex items-center" style={{ gap: 6 }}>
            <Chip tone="rust" icon={<Flame size={14} className="streak-fire" />}>{stats.streak || 0}d</Chip>
            <Chip tone="sky" icon={<Gem size={14} />}>{stats.gems ?? 50}</Chip>
          </div>

          {!isPremium() && (
            <Link to="/pricing" className="hidden md:inline-flex" style={{ textDecoration: "none" }}>
              <Chip tone="ink" icon={<Crown size={11} />}>Premium</Chip>
            </Link>
          )}

          {/* Profile menu */}
          {user && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen((o) => !o)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "transparent", border: 0, cursor: "pointer",
                  padding: 4, borderRadius: 999,
                }}
                aria-label="Profile menu"
              >
                <Avatar initials={initial} color="rust" size={36} ringColor="var(--gold-500)" />
                <ChevronDown size={14} color="var(--fg-3)" className="hidden md:block" />
              </button>

              {profileOpen && (
                <>
                  <div className="fixed inset-0" onClick={() => setProfileOpen(false)} />
                  <div
                    style={{
                      position: "absolute", right: 0, marginTop: 8,
                      width: 240,
                      background: "#fff",
                      borderRadius: "var(--radius-lg)",
                      border: "1px solid var(--border-2)",
                      boxShadow: "var(--shadow-card)",
                      padding: 6,
                      zIndex: 60,
                    }}
                  >
                    <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border-1)" }}>
                      <p style={{ fontFamily: "var(--font-display)", fontSize: 18, lineHeight: 1.1, margin: 0, color: "var(--fg-1)" }}>{user.name}</p>
                      <p style={{ fontSize: 12, color: "var(--fg-3)", margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis" }}>{user.email}</p>
                    </div>
                    <Link to="/profile" className="se-menu-item" onClick={() => setProfileOpen(false)}><UserRound size={16} /> Profile</Link>
                    <Link to="/settings" className="se-menu-item" onClick={() => setProfileOpen(false)}><Settings2 size={16} /> Settings</Link>
                    {user?.isAdmin && (
                      <Link to="/admin" className="se-menu-item" onClick={() => setProfileOpen(false)}><Shield size={16} /> Admin</Link>
                    )}
                    {setDarkMode && (
                      <button type="button" onClick={() => setDarkMode(!darkMode)} className="se-menu-item" style={{ width: "100%", border: 0, background: "transparent", textAlign: "left" }}>
                        {darkMode ? <Sun size={16} /> : <Moon size={16} />} {darkMode ? "Light" : "Dark"} mode
                      </button>
                    )}
                    <div style={{ borderTop: "1px solid var(--border-1)", marginTop: 4, paddingTop: 4 }}>
                      <button type="button" onClick={() => { onLogout(); setProfileOpen(false); }} className="se-menu-item" style={{ width: "100%", border: 0, background: "transparent", textAlign: "left", color: "var(--berry)" }}>
                        <LogOut size={16} /> Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="md:hidden"
            aria-label="Menu"
            style={{ background: "transparent", border: 0, padding: 8, cursor: "pointer", color: "var(--fg-2)" }}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="md:hidden"
          style={{
            background: "var(--paper)",
            borderTop: "1px solid var(--border-1)",
            padding: "12px 16px",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {PRIMARY_LINKS.filter((l) => l.to !== "/pricing").map((l) => (
            <Link key={l.label} to={l.to}
              style={{ display: "block", padding: "12px 12px", color: "var(--fg-1)", fontWeight: 600, fontSize: 15, textDecoration: "none", borderRadius: "var(--radius-md)" }}>
              {l.label}
            </Link>
          ))}
          <div style={{ borderTop: "1px solid var(--border-1)", marginTop: 6, paddingTop: 6 }}>
            <Link to="/pricing" style={{ display: "block", padding: "10px 12px", color: "var(--fg-2)", fontWeight: 500, fontSize: 14, textDecoration: "none" }}>Pricing</Link>
            <Link to="/about" style={{ display: "block", padding: "10px 12px", color: "var(--fg-2)", fontWeight: 500, fontSize: 14, textDecoration: "none" }}>About</Link>
            <Link to="/contact" style={{ display: "block", padding: "10px 12px", color: "var(--fg-2)", fontWeight: 500, fontSize: 14, textDecoration: "none" }}>Contact</Link>
            <Link to="/profile" style={{ display: "block", padding: "10px 12px", color: "var(--fg-2)", fontWeight: 500, fontSize: 14, textDecoration: "none" }}>Profile</Link>
            <Link to="/settings" style={{ display: "block", padding: "10px 12px", color: "var(--fg-2)", fontWeight: 500, fontSize: 14, textDecoration: "none" }}>Settings</Link>
            {user?.isAdmin && (
              <Link to="/admin" style={{ display: "block", padding: "10px 12px", color: "var(--fg-2)", fontWeight: 500, fontSize: 14, textDecoration: "none" }}>Admin</Link>
            )}
            <button type="button" onClick={onLogout}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 12px", color: "var(--berry)", fontWeight: 500, fontSize: 14, background: "transparent", border: 0, cursor: "pointer" }}>
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* Tiny inline styles for menu items */}
      <style>{`
        .se-menu-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; font-size: 14px; color: var(--fg-1);
          text-decoration: none; border-radius: 8px;
          transition: background 150ms cubic-bezier(.2,.8,.2,1);
        }
        .se-menu-item:hover { background: var(--paper-deep); }
      `}</style>
    </nav>
  );
}
